import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const { API } = useAuth();
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid"); setOrderId(data.order_id); clear(); return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 8) { setStatus("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 8) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  return (
    <div data-testid="checkout-success-page" className="max-w-2xl mx-auto px-6 py-24 text-center">
      {status === "checking" && (
        <div data-testid="success-checking">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#6B1A24]" strokeWidth={1.5} />
          <h1 className="font-display text-4xl mt-6">Confirming your payment…</h1>
          <p className="text-sm text-[#5C5454] mt-4">Please don't close this window.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="success-paid">
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#3A5A40]" strokeWidth={1.2} />
          <div className="label-eyebrow mt-8">Order Placed</div>
          <h1 className="font-display text-5xl mt-3">Thank you.</h1>
          <p className="text-sm text-[#5C5454] mt-4">Your order has been received and is being prepared with care.<br />Order ID: <span className="font-mono text-[#2D2626]">{orderId}</span></p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/orders" data-testid="success-view-orders" className="bg-[#6B1A24] text-[#FAFAF7] px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#8A2532] transition-colors">View Orders</Link>
            <Link to="/shop" data-testid="success-continue" className="border border-[#2D2626] px-8 py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#2D2626] hover:text-[#FAFAF7] transition-colors">Continue shopping</Link>
          </div>
        </div>
      )}
      {(status === "pending" || status === "expired" || status === "error") && (
        <div data-testid={`success-${status}`}>
          <h1 className="font-display text-4xl mt-6">{status === "pending" ? "Payment still processing" : status === "expired" ? "Session expired" : "We couldn't confirm your payment"}</h1>
          <p className="text-sm text-[#5C5454] mt-4">Please check your orders page or try again.</p>
          <Link to="/orders" className="inline-block mt-8 bg-[#6B1A24] text-[#FAFAF7] px-8 py-4 uppercase text-xs tracking-[0.25em]">Go to Orders</Link>
        </div>
      )}
    </div>
  );
}
